/**
 * @fileoverview Read-only view of the teacher's lesson assessment for the logged-in student (AR-898,
 * "View" on the Teacher assessment column), in the My tasks drawer pattern (StudentTaskDrawerShell,
 * Mantine only). Built on the same legacy data the self-assessment drawer already reads, so no new
 * backend endpoint is needed.
 */

import { useEffect, useState } from 'react'
import { Group, Paper, Stack, Text, Title } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { AssessmentDrawerSkeleton, StudentTaskDrawerShell } from 'pages/StudentTasks/components'
import { lessonLabel } from 'pages/StudentTasks/utils'
import { fetchTeacherAssessment } from './service'
import { TeacherScoreItemCard } from './TeacherScoreItemCard'
import type { TeacherAssessmentData, TeacherScoreItem } from './types'

interface StudentTeacherAssessmentDrawerProps {
  opened: boolean
  lessonId: string
  lessonTitle?: string
  /** `YYYY-MM-DD` */
  lessonDate?: string
  assessmentId: string | null
  studentId: string
  projectName?: string
  onClose: () => void
}

interface BodyProps {
  lessonId: string
  assessmentId: string | null
  studentId: string
  lesson: string
  projectName?: string
}

function Section({ title, items, color }: { title: string; items: TeacherScoreItem[]; color: 'blue' | 'green' }) {
  if (items.length === 0) return null
  return (
    <Stack spacing='md'>
      <Title order={5}>{title}</Title>
      {items.map(item => (
        <TeacherScoreItemCard key={item.id} item={item} color={color} />
      ))}
    </Stack>
  )
}

function TeacherAssessmentBody({ lessonId, assessmentId, studentId, lesson, projectName }: BodyProps) {
  const { t } = useTranslation()
  const tr = (key: string) => t(`student_task_tracker.teacher_drawer.${key}`)
  const [data, setData] = useState<TeacherAssessmentData | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setData(null)
    setFailed(false)
    fetchTeacherAssessment(lessonId, studentId, assessmentId)
      .then(result => {
        if (!cancelled) setData(result)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [lessonId, studentId, assessmentId])

  if (failed) {
    return (
      <Text color='dimmed' align='center' py='xl'>
        {tr('unavailable')}
      </Text>
    )
  }
  if (!data) return <AssessmentDrawerSkeleton />

  const hasItems = data.standards.length > 0 || data.skills.length > 0

  return (
    <Stack spacing='lg'>
      <Paper withBorder radius='sm' p='xs'>
        <Stack spacing={2}>
          {projectName && (
            <Group spacing={4}>
              <Text size='sm' color='dimmed'>
                {t('student_tasks.assessment_drawer.project')}:
              </Text>
              <Text size='sm'>{projectName}</Text>
            </Group>
          )}
          {lesson && (
            <Group spacing={4}>
              <Text size='sm' color='dimmed'>
                {t('student_tasks.assessment_drawer.lesson')}:
              </Text>
              <Text size='sm'>{lesson}</Text>
            </Group>
          )}
        </Stack>
      </Paper>

      <Section title={tr('skills')} items={data.skills} color='blue' />
      <Section title={tr('standards')} items={data.standards} color='green' />

      {!hasItems && <Text color='dimmed'>{tr('no_items')}</Text>}

      {data.journal && (
        <Stack spacing='xs'>
          <Title order={5}>{tr('journal')}</Title>
          <Text size='sm' sx={{ whiteSpace: 'pre-line' }}>
            {data.journal}
          </Text>
        </Stack>
      )}
    </Stack>
  )
}

export function StudentTeacherAssessmentDrawer({
  opened,
  lessonId,
  lessonTitle,
  lessonDate,
  assessmentId,
  studentId,
  projectName,
  onClose,
}: StudentTeacherAssessmentDrawerProps) {
  const { t } = useTranslation()

  return (
    <StudentTaskDrawerShell opened={opened} title={t('student_task_tracker.teacher_drawer.title')} onClose={onClose}>
      {/* Mounted only while open so each opening starts a fresh fetch. */}
      {opened && lessonId && studentId && (
        <TeacherAssessmentBody
          lessonId={lessonId}
          assessmentId={assessmentId}
          studentId={studentId}
          lesson={lessonTitle ? lessonLabel(lessonDate, lessonTitle) : ''}
          projectName={projectName}
        />
      )}
    </StudentTaskDrawerShell>
  )
}
